import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { withTranslation, WithTranslation } from 'react-i18next';

// Catches render crashes so production shows a way forward instead of a blank
// white page. Pass `resetKey` (e.g. the pathname) so navigating clears a crash.
interface Props extends WithTranslation {
  children: ReactNode;
  // Clears the error when it changes. Unlike a `key`, it doesn't remount the
  // children, so a page that changes its URL (e.g. opening a drawer) keeps its state.
  resetKey?: string;
  // Full-page (no layout around it) vs inside the dashboard content area.
  fullPage?: boolean;
  // Shown instead of the recovery screen, e.g. null for the top bar / sidebar
  // so a crash there hides that piece and the page keeps working.
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

// A lazy page chunk that no longer exists on the server (a deploy happened
// while the tab was open). A reload fetches the new version.
const isChunkError = (e: Error | null) => !!e && /ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk/i.test(`${e.name} ${e.message}`);

class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Details for developers / log collection; never shown to the user.
    console.error('[UI crash]', error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    const { children, fullPage, fallback, t } = this.props;
    if (!error) return children;
    if (fallback !== undefined) return fallback;

    const chunk = isChunkError(error);
    return (
      <Box
        role="alert"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        px={2}
        py={8}
        minHeight={fullPage ? '100vh' : 360}
        bgcolor={fullPage ? 'background.default' : undefined}
      >
        <ErrorOutlineIcon color="error" style={{ fontSize: 44, marginBottom: 12 }} />
        <Typography variant="h4" color="textPrimary" gutterBottom>
          {chunk ? t('A new version is available') : t('Something went wrong on this page')}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ maxWidth: 440, marginBottom: 24 }}>
          {chunk
            ? t('The dashboard was updated while this tab was open. Reload to get the latest version.')
            : t('The problem has been logged. Reload the page, or go back to the dashboard. If it keeps happening, contact support.')}
        </Typography>
        <Box display="flex" style={{ gap: 8 }}>
          {!chunk && (
            // Re-render just this page; often enough once data has arrived.
            <Button variant="contained" color="primary" onClick={() => this.setState({ error: null })}>
              {t('Try Again')}
            </Button>
          )}
          <Button variant={chunk ? 'contained' : 'outlined'} color={chunk ? 'primary' : 'default'} onClick={() => window.location.reload()}>
            {t('Reload Page')}
          </Button>
          {!chunk && (
            <Button variant="outlined" onClick={() => window.location.assign('/app/dashboard/overview')}>
              {t('Go to Dashboard')}
            </Button>
          )}
        </Box>
      </Box>
    );
  }
}

const ErrorBoundary = withTranslation()(ErrorBoundaryInner);
export default ErrorBoundary;
